/**
 * ONE RESEARCH TOPIC, ONE COMPLETE PAGE.
 *
 * THE OWNER'S FINAL ORGANIZATION. Research Control is a directory of subjects;
 * opening a subject shows everything the product knows about it on ONE page:
 * Scout's journal, its knowledge, experiments, evidence, hypotheses,
 * improvements and open questions. The section strip scrolls within this page;
 * it does not navigate away. Every table row expands IN PLACE to the full
 * record, so reading an entry never loses the topic.
 *
 * WHAT THE TABLES ARE, AND ARE NOT. A table is an INDEX: date, meaningful
 * title, status, a short summary. The full authored prose is behind the row,
 * rendered with the same Markdown component as the chat. A table of one-line
 * database labels standing in for Scout's writing would be the failure this
 * page exists to fix.
 *
 * PROVENANCE IS VISIBLE. Journal entries carry their authorship label; knowledge
 * carries its scientific status and evidence; evidence carries its quality and
 * episode; every section states whether its rows are DIRECTLY attributed to the
 * topic or linked at TARGET SCOPE. Nothing here invents a relation.
 *
 * TIMESTAMPS ARE THE RECORD'S. Dates are printed in the reader's locale from the
 * stored timestamp; the exact stored instant remains in the `dateTime`
 * attribute. An old record with no trustworthy creation time is not given one.
 */

import type { ComponentChildren } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { readGateway, type GatewayError } from '../gateway';
import { createLoadable } from '../resource';
import { Failure } from './failure';
import { PageHeader, Technical } from './about';
import { Markdown } from '../markdown';
import { navigate } from '../router';
import { formatTurnTime as formatRecordTime } from '../chat';

export const TOPIC_SECTION = 'topic';

interface JournalEntry {
	journalId: string;
	kind: 'OPENING' | 'PROGRESS' | 'CLOSING' | 'CONCLUSION';
	title: string | null;
	content: string;
	status: 'AUTHORED' | 'FAILED' | 'INCOMPLETE';
	statusNote: string | null;
	author: 'SCOUT' | 'HARNESS';
	evidenceRefs: string[];
	createdAt: string;
}

interface KnowledgeEntry {
	entryId: string;
	title: string;
	learned: string;
	whyItMatters: string;
	limitations: string;
	knowledgeStatus: string;
	statusNote: string | null;
	evidenceRefs: string[];
	workerReportRefs: string[];
	supersedesEntryId: string | null;
	supersededByEntryId: string | null;
	revision: number;
	createdAt: string;
}

interface EpisodeRow {
	episodeId: string;
	question: string;
	status: string;
	createdAt: string;
}

/** The label a row carries and, when another topic owns its ORIGIN, which
 *  topics those are. `linkage` remains the rendered label. */
interface ProvenanceFields {
	linkage: string;
	attribution?: string;
	originTopicIds?: string[];
	originTopicTitles?: string[];
}

interface ExperimentRow extends ProvenanceFields {
	experimentId: string;
	question: string;
	status: string;
	verdict: string | null;
	prediction: string;
	expectedObservation: string;
	falsifier: string | null;
	environmentJson: string | null;
	receiptJson: string | null;
	evaluationJson: string | null;
	createdAt: string;
	updatedAt: string;
}

interface EvidenceRow {
	sourceId: string;
	locator: string;
	title: string | null;
	quality: string;
	claimSummary: string;
	retrievedAt: string;
	episodeId: string;
	linkage: string;
	evidenceOrigin?: string;
	originEpisodeId?: string | null;
	originTopicId?: string | null;
	originTopicTitle?: string | null;
}

interface HypothesisRow extends ProvenanceFields {
	hypothesisId: string;
	statement: string;
	status: string;
	confidence: string;
	discriminators: string[];
	convergenceReason: string | null;
	createdAt: string;
	lastRevisedAt: string | null;
}

interface ImprovementRow extends ProvenanceFields {
	candidateId: string;
	title: string;
	summary: string;
	kind: string | null;
	status: string;
	impact: string;
	confidence: string;
	review: { decision: string; reason: string; at: string } | null;
	implementationEvidence: { changeRef: string } | null;
	verificationEvidence: { method: string; result: string; testRef: string } | null;
	firstDetectedAt: string;
	lastDetectedAt: string;
}

interface OpenQuestionRow extends ProvenanceFields {
	unknownKey: string;
	statement: string;
	recordedAt: string;
	resolvedAt: string | null;
}

interface TopicPage {
	topic: {
		topicId: string;
		/** NULL = Main Scout. The topic page names the workspace it belongs to
		 *  and links there; a subject is not free-floating. */
		spaceId?: string | null;
		title: string;
		objective: string;
		summary: string;
		status: string;
		targetKey: string | null;
		createdAt: string;
		updatedAt: string;
		lastActivityAt: string;
		author: string;
		provenance: string;
	};
	journal: JournalEntry[];
	knowledge: KnowledgeEntry[];
	episodes: EpisodeRow[];
	experiments: ExperimentRow[];
	evidence: EvidenceRow[];
	hypotheses: HypothesisRow[];
	improvements: ImprovementRow[];
	openQuestions: OpenQuestionRow[];
	linkageNotes: Record<string, string>;
}

interface Payload {
	wired: boolean;
	reason?: string;
	topic?: TopicPage['topic'] | null;
	journal?: JournalEntry[];
	knowledge?: KnowledgeEntry[];
	episodes?: EpisodeRow[];
	experiments?: ExperimentRow[];
	evidence?: EvidenceRow[];
	hypotheses?: HypothesisRow[];
	improvements?: ImprovementRow[];
	openQuestions?: OpenQuestionRow[];
	linkageNotes?: Record<string, string>;
	/** THE EMBEDDED EPISODE PAGE AND WHAT REMAINS (H-036). The server sends the
	 *  first bounded page plus the real total; the episodes route filtered by
	 *  `topicId` is the continuation. */
	episodesTotal?: number;
	episodesOffset?: number;
	episodesLimit?: number;
	episodesHasMore?: boolean;
}

/** THE SEVEN RESEARCH TABLES, IN THE OWNER'S ORDER. Investigations follows
 *  them because it is the episode index the evidence was read through, not a
 *  seventh research output; it stays visible so the table above it can be
 *  traced back to the window that produced it. */
const SECTIONS = [
	{ id: 'journal', label: 'Journal' },
	{ id: 'knowledge', label: 'Knowledge' },
	{ id: 'experiments', label: 'Experiments' },
	{ id: 'evidence', label: 'Evidence' },
	{ id: 'hypotheses', label: 'Hypotheses' },
	{ id: 'improvements', label: 'Improvements' },
	{ id: 'open-questions', label: 'Open Questions' },
	{ id: 'episodes', label: 'Investigations' },
] as const;

/** WHAT THIS ROW'S CONNECTION TO THE TOPIC ACTUALLY IS, in one phrase. An
 *  ORIGIN in another topic is named, because "target scope" would hide the
 *  strongest true fact about the record behind a weaker one. */
function provenanceText(row: ProvenanceFields): string {
	const origin = (row.originTopicTitles ?? []).filter((title) => title.length > 0);
	if (row.attribution === 'ORIGIN_ELSEWHERE' && origin.length > 0) {
		return `${row.linkage} — produced by ${origin.join(', ')}`;
	}
	if (row.attribution === 'ORIGIN') return 'ORIGIN — produced under this topic';
	if (row.attribution === 'RELATED') return 'RELATED — an explicit cross-topic link';
	if (row.attribution === 'TARGET_SCOPE') return 'TARGET_SCOPE — shares this topic’s target; no origin is claimed';
	return row.linkage;
}

function statusClass(status: string): string {
	const upper = status.toUpperCase();
	if (['SUPPORTED', 'COMPLETED', 'ACCEPTED', 'VERIFIED', 'CONVERGED'].includes(upper)) {
		return 'tag tag-live';
	}
	if (
		['REFUTED', 'FAILED', 'BLOCKED', 'CANCELLED', 'DISMISSED', 'UNRESOLVED', 'DISPUTED', 'PROVISIONAL'].includes(
			upper,
		)
	) {
		return 'tag tag-warn';
	}
	return 'tag';
}

interface TopicRow {
	key: string;
	date: string;
	title: string;
	status: string;
	summary: string;
	detail: ComponentChildren;
}

/**
 * THE TABLE, AND THE EXPANSION. A row is a button; the full record appears in
 * a spanning row directly beneath it. One row open per section keeps a long
 * document readable without hiding the index.
 */
function TopicTable(input: {
	id: string;
	title: string;
	note: string;
	empty: string;
	columns: readonly string[];
	rows: readonly TopicRow[];
	headerExtra?: ComponentChildren;
	/** A continuation control, rendered under the table (H-036). */
	footer?: ComponentChildren;
	/** THE PAGE'S RECORD FILTER. One query narrows every section by the same
	 *  words, so a reader can find a record without opening eight tables in
	 *  turn. It filters only rows already loaded; it never claims the corpus
	 *  holds nothing outside the page. */
	filter?: string;
}) {
	const [open, setOpen] = useState<string | null>(null);
	const wanted = (input.filter ?? '').trim().toLowerCase();
	const rows =
		wanted.length === 0
			? input.rows
			: input.rows.filter((row) =>
					`${row.title} ${row.summary} ${row.status}`.toLowerCase().includes(wanted),
				);
	return (
		<section class="panel topic-section" id={input.id} aria-labelledby={`${input.id}-heading`}>
			<div class="topic-section-head">
				<h2 id={`${input.id}-heading`}>
					{input.title}
					<span class="section-count" aria-label={`${rows.length} records`}>
						{rows.length}
					</span>
				</h2>
				{input.headerExtra}
			</div>
			<p class="section-note">{input.note}</p>
			{rows.length === 0 ? (
				<div class="empty-state">
					<p class="empty-note">
						{input.rows.length > 0 && wanted.length > 0
							? `No record in this section matches “${input.filter ?? ''}”. The section holds ${input.rows.length} record(s); the filter is not a statement about the corpus.`
							: input.empty}
					</p>
				</div>
			) : (
				<div class="topic-table-wrap">
					<table class="topic-table data-table">
						<thead>
							<tr>
								{input.columns.map((column) => (
									<th key={column}>{column}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const expanded = open === row.key;
								return (
									<>
										<tr
											key={row.key}
											class={expanded ? 'is-open' : undefined}
											onClick={() => setOpen(expanded ? null : row.key)}
											onKeyDown={(event: KeyboardEvent) => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault();
													setOpen(expanded ? null : row.key);
												}
											}}
											tabIndex={0}
											aria-expanded={expanded}
										>
											<td class="topic-date" data-label={input.columns[0]}>
												<time dateTime={row.date}>{formatRecordTime(row.date)}</time>
											</td>
											<td class="topic-title" data-label={input.columns[1]}>
												<span class="topic-row-title">{row.title}</span>
												{row.summary.length > 0 && (
													<span class="topic-row-summary">{row.summary}</span>
												)}
											</td>
											<td data-label={input.columns[2]}>
												<span class={statusClass(row.status)}>{row.status}</span>
											</td>
										</tr>
										{expanded && (
											<tr key={`${row.key}:detail`} class="topic-detail">
												<td colSpan={input.columns.length}>{row.detail}</td>
											</tr>
										)}
									</>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			{input.footer !== undefined && <div class="topic-section-footer">{input.footer}</div>}
		</section>
	);
}

function RefList({ refs }: { refs: readonly string[] }) {
	if (refs.length === 0) return null;
	return (
		<Technical summary={`${refs.length} evidence reference(s)`}>
			<ul class="references">
				{refs.map((ref) => (
					<li key={ref}>
						<code>{ref}</code>
					</li>
				))}
			</ul>
		</Technical>
	);
}

export function ResearchTopicView({ topicId }: { topicId: string | null }) {
	const page = useMemo(
		() =>
			createLoadable<Payload>(() =>
				topicId === null
					? Promise.resolve({ ok: true as const, data: null as never })
					: readGateway<Payload>(
							`/api/scout/gateway/research/topics/${encodeURIComponent(topicId)}`,
						),
			),
		[topicId],
	);
	const spacesRead = useMemo(
		() =>
			createLoadable<{ spaces: Array<{ spaceId: string; name: string }> }>(() =>
				readGateway('/api/scout/gateway/spaces?limit=200'),
			),
		[],
	);
	useEffect(() => {
		if (topicId !== null) void page.load();
	}, [page, topicId]);
	useEffect(() => {
		void spacesRead.load();
	}, [spacesRead]);
	const [journalOldestFirst, setJournalOldestFirst] = useState(true);
	/** ONE FILTER OVER EVERY SECTION. It narrows the rows each section has
	 *  already loaded; it does not ask the server a second question and never
	 *  renders an unread corpus as empty. */
	const [recordFilter, setRecordFilter] = useState('');
	/** THE EPISODE CONTINUATION (H-036). The page embeds one bounded page of
	 *  the topic's investigations; `episodesHasMore` and the topic-filtered
	 *  episodes route are how the rest is reached. */
	const [episodesOlder, setEpisodesOlder] = useState<EpisodeRow[]>([]);
	const [episodesMore, setEpisodesMore] = useState(false);
	const [episodesPaging, setEpisodesPaging] = useState(false);
	const [episodesPageError, setEpisodesPageError] = useState<GatewayError | null>(null);
	const state = page.state.value;
	useEffect(() => {
		setEpisodesOlder([]);
		setEpisodesMore(false);
		setEpisodesPageError(null);
	}, [topicId]);
	const loadMoreEpisodes = useCallback(async () => {
		if (topicId === null || state.status !== 'ready' || state.data === null) return;
		setEpisodesPaging(true);
		setEpisodesPageError(null);
		const offset = (state.data.episodes?.length ?? 0) + episodesOlder.length;
		const result = await readGateway<{ episodes: EpisodeRow[]; hasMore?: boolean }>(
			`/api/scout/gateway/research/episodes?topicId=${encodeURIComponent(topicId)}&limit=200&offset=${offset}`,
		);
		setEpisodesPaging(false);
		if (!result.ok) {
			setEpisodesPageError(result.error);
			return;
		}
		setEpisodesOlder((previous) => [...previous, ...result.data.episodes]);
		setEpisodesMore(result.data.hasMore === true);
	}, [topicId, state, episodesOlder]);

	if (topicId === null) {
		return (
			<section class="view">
				<PageHeader title="Research topic" about={{ purpose: 'One research subject and everything connected to it.' }} />
				<p class="notice">This address names no topic.</p>
			</section>
		);
	}

	if (state.status === 'loading') {
		return (
			<section class="view">
				<p class="notice" role="status">
					Reading the research topic…
				</p>
			</section>
		);
	}
	if (state.status === 'failed' && state.error !== null) {
		return (
			<section class="view">
				<Failure error={state.error} what="this research topic" onRetry={() => void page.load()} />
			</section>
		);
	}
	const data = state.data;
	if (data === null) return null;
	if (!data.wired || data.topic === undefined || data.topic === null) {
		return (
			<section class="view">
				<PageHeader title="Research topic" about={{ purpose: 'One research subject and everything connected to it.' }} />
				<p class="notice">
					<strong>This topic is not on record in this deployment.</strong>{' '}
					{data.reason ?? 'The address may name a topic from another owner or another store.'}
				</p>
			</section>
		);
	}
	const topic = data.topic;
	const workspaceName =
		topic.spaceId === null || topic.spaceId === undefined
			? 'Main Scout'
			: (spacesRead.state.value.data?.spaces.find((space) => space.spaceId === topic.spaceId)?.name ??
				topic.spaceId);
	const journal = data.journal ?? [];
	const knowledge = data.knowledge ?? [];
	const notes = data.linkageNotes ?? {};
	const firstEpisodes = data.episodes ?? [];
	const seenEpisodes = new Set(firstEpisodes.map((episode) => episode.episodeId));
	const episodeList = [
		...firstEpisodes,
		...episodesOlder.filter((episode) => !seenEpisodes.has(episode.episodeId)),
	];
	const episodesHasMore =
		episodesMore || (episodesOlder.length === 0 && data.episodesHasMore === true);
	const episodeRows: TopicRow[] = episodeList.map((episode) => ({
		key: episode.episodeId,
		date: episode.createdAt,
		title: episode.question,
		status: episode.status,
		summary: `investigation ${episode.episodeId}`,
		detail: (
			<div class="topic-detail-body">
				<p>
					<strong>Question.</strong> {episode.question}
				</p>
				<p class="muted small">
					episode <code>{episode.episodeId}</code> · opened{' '}
					<time dateTime={episode.createdAt}>{formatRecordTime(episode.createdAt)}</time>
				</p>
				<p>
					<a href={`/research/observatory/${encodeURIComponent(episode.episodeId)}`}>
						Open this investigation’s evidence
					</a>
				</p>
			</div>
		),
	}));
	const journalRows: TopicRow[] = [...journal]
		.sort((left, right) =>
			journalOldestFirst
				? Date.parse(left.createdAt) - Date.parse(right.createdAt)
				: Date.parse(right.createdAt) - Date.parse(left.createdAt),
		)
		.map((entry) => ({
			key: entry.journalId,
			date: entry.createdAt,
			title:
				entry.title ??
				(entry.kind === 'OPENING'
					? 'Opening entry'
					: entry.kind === 'PROGRESS'
						? 'Progress entry'
						: entry.kind === 'CONCLUSION'
							? 'Scout’s conclusion'
							: 'Final synthesis'),
			status: entry.author === 'SCOUT' ? 'SCOUT-AUTHORED' : entry.status,
			summary:
				entry.status === 'AUTHORED'
					? `${entry.content.replace(/[#*_`>|]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 180)}…`
					: (entry.statusNote ?? 'No content was recorded.'),
			detail: (
				<div class="topic-detail-body">
					<p class="muted small">{formatRecordTime(entry.createdAt)}</p>
					{entry.content.length > 0 && (
						<Markdown content={entry.content} class="turn-markdown authored-content" />
					)}
					{entry.status !== 'AUTHORED' && entry.statusNote !== null && (
						<p class="notice small">
							<strong>{entry.status === 'FAILED' ? 'This entry was not written.' : 'Incomplete.'}</strong>{' '}
							{entry.statusNote}
						</p>
					)}
					<p class="muted small">
						{entry.author === 'SCOUT'
							? 'Scout wrote this entry.'
							: 'Harness execution metadata — not Scout’s words.'}
					</p>
					<RefList refs={entry.evidenceRefs} />
				</div>
			),
		}));

	const knowledgeRows: TopicRow[] = knowledge.map((entry) => ({
		key: entry.entryId,
		date: entry.createdAt,
		title: entry.title,
		status: entry.knowledgeStatus,
		summary: entry.learned.replace(/[#*_`>|]/g, ' ').replace(/\s+/g, ' ').slice(0, 180),
		detail: (
			<div class="topic-detail-body">
				<Markdown content={entry.learned} class="turn-markdown authored-content" />
				<p>
					<strong>Why it matters.</strong> {entry.whyItMatters}
				</p>
				<p>
					<strong>What the evidence does not establish.</strong> {entry.limitations}
				</p>
				{entry.statusNote !== null && <p class="notice small">{entry.statusNote}</p>}
				<p class="muted small">
					Scout wrote this {formatRecordTime(entry.createdAt)} · revision {entry.revision}
					{entry.supersedesEntryId !== null && <> · supersedes <code>{entry.supersedesEntryId}</code></>}
					{entry.supersededByEntryId !== null && (
						<> · superseded by <code>{entry.supersededByEntryId}</code></>
					)}
				</p>
				<RefList refs={entry.evidenceRefs} />
				<RefList refs={entry.workerReportRefs} />
				<p class="small">
					<a href={`/discoveries/knowledge#${encodeURIComponent(entry.entryId)}`}>
						Open in the global knowledge library
					</a>
				</p>
			</div>
		),
	}));

	const experimentRows: TopicRow[] = (data.experiments ?? []).map((experiment) => ({
		key: experiment.experimentId,
		date: experiment.createdAt,
		title: experiment.question,
		status: experiment.status,
		summary: experiment.verdict ?? 'No verdict recorded.',
		detail: (
			<div class="topic-detail-body">
				<p><strong>Prediction (fixed before execution).</strong> {experiment.prediction}</p>
				<p><strong>Expected observation.</strong> {experiment.expectedObservation}</p>
				{experiment.falsifier !== null && <p><strong>Falsifier.</strong> {experiment.falsifier}</p>}
				<p class="muted small">
					created {formatRecordTime(experiment.createdAt)} · updated{' '}
					{formatRecordTime(experiment.updatedAt)} · {provenanceText(experiment)}
				</p>
				{experiment.receiptJson !== null && (
					<Technical summary="Execution receipt">
						<pre class="report">{experiment.receiptJson.slice(0, 6_000)}</pre>
					</Technical>
				)}
				<p class="muted small">
					A receipt is an execution record, not a scientific conclusion. A completed run does not
					verify the hypothesis.
				</p>
			</div>
		),
	}));

	const evidenceRows: TopicRow[] = (data.evidence ?? []).map((source) => ({
		key: source.sourceId,
		date: source.retrievedAt,
		title: source.title ?? source.locator,
		status: source.quality,
		summary: source.claimSummary.slice(0, 180),
		detail: (
			<div class="topic-detail-body">
				<p class="small"><code>{source.locator}</code></p>
				<p>{source.claimSummary}</p>
				<p class="muted small">
					Recorded {formatRecordTime(source.retrievedAt)} · episode <code>{source.episodeId}</code> ·{' '}
					{source.evidenceOrigin === 'REUSE'
						? `REUSE — another topic (${source.originTopicTitle ?? source.originTopicId ?? 'unrecorded'}) recorded this canonical source first`
						: source.evidenceOrigin === 'ORIGIN'
							? 'ORIGIN — this topic’s investigation recorded this canonical source first'
							: 'source origin not reported'}. A source is not automatically relevant, and a
					retrieved source is not a finding.
				</p>
			</div>
		),
	}));

	const hypothesisRows: TopicRow[] = (data.hypotheses ?? []).map((hypothesis) => ({
		key: hypothesis.hypothesisId,
		date: hypothesis.createdAt,
		title: hypothesis.statement,
		status: hypothesis.status,
		summary: `confidence ${hypothesis.confidence.toLowerCase()}`,
		detail: (
			<div class="topic-detail-body">
				{hypothesis.discriminators.length > 0 && (
					<p><strong>Discriminators.</strong> {hypothesis.discriminators.join(' · ')}</p>
				)}
				{hypothesis.convergenceReason !== null && <p>{hypothesis.convergenceReason}</p>}
				<p class="muted small">
					created {formatRecordTime(hypothesis.createdAt)}
					{hypothesis.lastRevisedAt !== null && <> · revised {formatRecordTime(hypothesis.lastRevisedAt)}</>}
					{' '}· {provenanceText(hypothesis)}
				</p>
			</div>
		),
	}));

	const improvementRows: TopicRow[] = (data.improvements ?? []).map((candidate) => ({
		key: candidate.candidateId,
		date: candidate.lastDetectedAt,
		title: candidate.title,
		status: candidate.status,
		summary: candidate.summary.slice(0, 180),
		detail: (
			<div class="topic-detail-body">
				<p>{candidate.summary}</p>
				<p class="muted small">
					kind {candidate.kind ?? 'unclassified'} · impact {candidate.impact} · confidence{' '}
					{candidate.confidence.toLowerCase()} · detected {formatRecordTime(candidate.firstDetectedAt)}
				</p>
				{candidate.review !== null && (
					<p class="small">
						<strong>Owner review.</strong> {candidate.review.decision} — {candidate.review.reason}
					</p>
				)}
				{candidate.implementationEvidence !== null && (
					<p class="small"><strong>Implementation.</strong> <code>{candidate.implementationEvidence.changeRef}</code></p>
				)}
				{candidate.verificationEvidence !== null && (
					<p class="small">
						<strong>Verification.</strong> {candidate.verificationEvidence.method} —{' '}
						{candidate.verificationEvidence.result}
					</p>
				)}
				<p class="muted small">
					Scout proposes; it cannot implement, verify or deploy its own improvement.{' '}
					{provenanceText(candidate)}.
				</p>
			</div>
		),
	}));

	const openQuestionRows: TopicRow[] = (data.openQuestions ?? []).map((question) => ({
		key: question.unknownKey,
		date: question.recordedAt,
		title: question.statement,
		status: question.resolvedAt === null ? 'OPEN' : 'RESOLVED',
		summary: question.resolvedAt === null ? 'Still unresolved.' : 'Resolved.',
		detail: (
			<div class="topic-detail-body">
				<p>{question.statement}</p>
				<p class="muted small">
					recorded {formatRecordTime(question.recordedAt)}
					{question.resolvedAt !== null && <> · resolved {formatRecordTime(question.resolvedAt)}</>} ·{' '}
					{provenanceText(question)}. An open question is not knowledge.
				</p>
			</div>
		),
	}));

	return (
		<section class="view topic-page" aria-labelledby="topic-heading">
			<p class="small">
				<a
					href="/research"
					onClick={(event: MouseEvent) => {
						event.preventDefault();
						navigate({ destination: 'research', section: null, recordId: null });
					}}
				>
					← All research topics
				</a>
			</p>
			<PageHeader
				id="topic-heading"
				title={topic.title}
				about={{
					purpose: 'One research subject and every scientific output connected to it.',
					how: (
						<p>
							The sections below read the canonical records: Scout’s journal and knowledge carry a
							topic attribution; evidence is read through the attributed episode; hypotheses,
							improvements and open questions are target-scope associations and are labelled as
							such. Click a row to read the full entry without leaving this page.
						</p>
					),
					limits: (
						<p>
							A topic page is a view, not a copy. The same knowledge entry appears in the global
							library, and one canonical record is never duplicated per topic. A table with no rows
							says so; nothing is invented to fill it.
						</p>
					),
				}}
			/>
			<div class="panel topic-summary">
				<div class="record-head">
					<span class={statusClass(topic.status)}>{topic.status}</span>
					<span class="tag">topic</span>
					{topic.spaceId === null || topic.spaceId === undefined ? (
						<span class="tag">Main Scout</span>
					) : (
						<a
							class="tag"
							href={`/spaces/${encodeURIComponent(topic.spaceId)}`}
							onClick={(event) => {
								if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
								event.preventDefault();
								navigate({ destination: 'spaces', section: topic.spaceId ?? null, recordId: null });
							}}
							title="Open this topic's research workspace"
						>
							{workspaceName}
						</a>
					)}
					<span class="muted small">
						created {formatRecordTime(topic.createdAt)} · last activity{' '}
						{formatRecordTime(topic.lastActivityAt)}
					</span>
				</div>
				<p class="record-summary clamp-3">{topic.objective}</p>
				<details class="topic-summary-more">
					<summary>
						Read the full objective{topic.summary.length > 0 ? ' and latest summary' : ''}
					</summary>
					<div class="topic-summary-body">
						<p>{topic.objective}</p>
						{topic.summary.length > 0 && <p>{topic.summary}</p>}
					</div>
				</details>
				{topic.targetKey !== null && (
					<Technical summary="Topic record">
						<p class="small">
							<code>{topic.topicId}</code> · target <code>{topic.targetKey}</code> · authored by{' '}
							{topic.author} · {topic.provenance}
						</p>
					</Technical>
				)}
			</div>

			<nav class="topic-nav" aria-label="Research sections">
				{SECTIONS.map((section) => (
					<a key={section.id} href={`#${section.id}`}>
						{section.label}
					</a>
				))}
			</nav>

			<div class="topic-filter" role="search">
				<label class="toolbar-field toolbar-field--grow">
					<span class="visually-hidden">Filter this topic's records</span>
					<input
						class="toolbar-input"
						type="search"
						placeholder="Filter every section's loaded records by title, summary or status…"
						value={recordFilter}
						onInput={(event) => setRecordFilter((event.target as HTMLInputElement).value)}
					/>
				</label>
				{recordFilter.length > 0 && (
					<button type="button" class="button ghost small" onClick={() => setRecordFilter('')}>
						Clear filter
					</button>
				)}
				<span class="section-note">
					Filters the records this page has loaded; it does not change what the server read.
				</span>
			</div>

			<TopicTable
				id="journal" filter={recordFilter}
				title="Research Journal"
				note={notes.journal ?? 'Scout’s own writing about this subject.'}
				empty="Scout has not written a journal entry attributed to this topic. An empty journal is honest; it is not filled with a generated narrative."
				columns={['Date', 'Entry', 'Type']}
				rows={journalRows}
				headerExtra={
					journal.length > 1 ? (
						<button
							type="button"
							class="button ghost small"
							onClick={() => setJournalOldestFirst((current) => !current)}
						>
							{journalOldestFirst ? 'Newest first' : 'Oldest first'}
						</button>
					) : null
				}
			/>
			<TopicTable
				id="knowledge" filter={recordFilter}
				title="Knowledge"
				note={notes.knowledge ?? 'Scout-authored knowledge entries attributed to this topic.'}
				empty="No knowledge entry is attributed to this topic. An open question or an inconclusive attempt is not promoted into one."
				columns={['Date', 'What Scout learned', 'Status']}
				rows={knowledgeRows}
			/>
			<TopicTable
				id="experiments" filter={recordFilter}
				title="Experiments"
				note={notes.experiments ?? 'Experiments linked to this topic’s hypotheses.'}
				empty="No experiment is linked to this topic. An unexecuted hypothesis is not shown as an experiment."
				columns={['Date', 'Experiment', 'Status']}
				rows={experimentRows}
			/>
			<TopicTable
				id="evidence" filter={recordFilter}
				title="Evidence"
				note={notes.evidence ?? 'Sources read by this topic’s attributed investigations.'}
				empty="No source is recorded through an investigation attributed to this topic. No source was invented to fill the section."
				columns={['Date', 'Source', 'Quality']}
				rows={evidenceRows}
			/>
			<TopicTable
				id="hypotheses" filter={recordFilter}
				title="Hypotheses"
				note={notes.hypotheses ?? 'Hypotheses recorded for this topic’s target.'}
				empty="No hypothesis is recorded for this topic’s target."
				columns={['Date', 'Hypothesis', 'Evaluation']}
				rows={hypothesisRows}
			/>
			<TopicTable
				id="improvements" filter={recordFilter}
				title="Improvements"
				note={notes.improvements ?? 'Improvement candidates recorded against this topic’s target.'}
				empty="No improvement candidate is recorded against this topic’s target."
				columns={['Date', 'Proposal', 'Lifecycle']}
				rows={improvementRows}
			/>
			<TopicTable
				id="open-questions" filter={recordFilter}
				title="Open Questions"
				note={notes.openQuestions ?? 'Questions recorded for this topic’s target.'}
				empty="No open question is recorded. That is not a claim that nothing remains unknown."
				columns={['Date', 'Question', 'Status']}
				rows={openQuestionRows}
			/>
			<TopicTable
				id="episodes" filter={recordFilter}
				title="Investigations"
				note="The investigations attributed to this topic. The evidence section above is derived from the episodes shown; the control below continues the episode list itself."
				empty="No investigation is attributed to this topic yet."
				columns={['Date', 'Investigation', 'Status']}
				rows={episodeRows}
				footer={
					<>
						{episodesHasMore && (
							<button
								type="button"
								class="button ghost"
								disabled={episodesPaging}
								onClick={() => void loadMoreEpisodes()}
							>
								{episodesPaging ? 'Reading the next page…' : 'Load more investigations'}
							</button>
						)}
						{data.episodesTotal !== undefined && (
							<p class="muted small">
								Showing {episodeList.length} of {data.episodesTotal} investigation(s).
							</p>
						)}
						{episodesPageError !== null && (
							<p class="notice small" role="status">
								The next page of investigations could not be read:{' '}
								{episodesPageError.message}
							</p>
						)}
					</>
				}
			/>
		</section>
	);
}
